import i18n from '@/utils/i18n';
import React, { useEffect, useRef } from 'react';
import { BORDER_RADIUS_MEDIUM, TYPOGRAPHY } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { ListOption } from './list-item';
import { TagPrintingTemplateImage } from './tag-printing-template-image-v2';
import { TagPrintingTemplateMetadata } from './tag-printing-types';
import { useLayoutContext } from '@/contexts/layout-context';

const TAG_PRINTING_TEMPLATE_MARGIN = 24;

export type TemplateListOption = ListOption<{
  metadata?: TagPrintingTemplateMetadata;
  previewImageUrlList: string[];
}>;

interface Props {
  onTemplateSelect: (option: TemplateListOption) => void;
  selectedTemplateUUID: string | null;
  templateOptions: TemplateListOption[];
}

export const TagPrintingTemplateCarousel: React.FC<Props> = ({
  onTemplateSelect,
  selectedTemplateUUID,
  templateOptions,
}) => {
  const [styles] = useStyles();
  const windowDimensions = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();

  const listRef = useRef<FlatList<TemplateListOption>>(null);

  const TAG_PRINTING_TEMPLATE_WIDTH = isSmallLayout
    ? windowDimensions.width * 0.5
    : windowDimensions.width * 0.4;
  const TAG_PRINTING_TEMPLATE_HEIGHT = TAG_PRINTING_TEMPLATE_WIDTH * 0.7;

  useEffect(() => {
    if (listRef.current && selectedTemplateUUID) {
      const foundTemplateIndex = templateOptions.findIndex(
        (template) => template.value === selectedTemplateUUID,
      );

      if (foundTemplateIndex !== -1) {
        listRef.current.scrollToIndex({
          animated: true,
          index: foundTemplateIndex,
        });
      }
    }
  }, [selectedTemplateUUID, templateOptions]);

  return templateOptions.length > 0 ? (
    <View
      style={{
        height: TAG_PRINTING_TEMPLATE_HEIGHT,
      }}
    >
      <FlatList
        data={templateOptions}
        extraData={selectedTemplateUUID}
        getItemLayout={(_, index) => ({
          index,
          length: TAG_PRINTING_TEMPLATE_WIDTH,
          offset: TAG_PRINTING_TEMPLATE_WIDTH * index,
        })}
        horizontal
        ref={listRef}
        renderItem={({ item }) => (
          <TouchableOpacity
            onPress={() => onTemplateSelect(item)}
            style={[
              selectedTemplateUUID === item.value
                ? styles.templateContainerBorderSelected
                : styles.templateContainerBorder,
              {
                width:
                  templateOptions.length === 1
                    ? windowDimensions.width - TAG_PRINTING_TEMPLATE_MARGIN * 2
                    : TAG_PRINTING_TEMPLATE_WIDTH,
              },
            ]}
            testID={`template-list-option-${item.value}`}
          >
            <TagPrintingTemplateImage
              isSelected={selectedTemplateUUID === item.value}
              previewImageUrlList={item.metadata?.previewImageUrlList ?? null}
              templateName={item.title}
            />
          </TouchableOpacity>
        )}
        showsHorizontalScrollIndicator={false}
      />
    </View>
  ) : (
    <View style={styles.noTemplatesText}>
      <Text style={TYPOGRAPHY.medium}>
        {i18n.t('no_tag_templates_available')}
      </Text>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  noTemplatesText: {
    justifyContent: 'flex-start',
    paddingVertical: 24,
  },
  templateContainerBorder: {
    borderColor: theme.colors.divider,
    borderRadius: BORDER_RADIUS_MEDIUM,
    borderWidth: 1,
    marginBottom: 4,
    marginRight: 8,
  },
  templateContainerBorderSelected: {
    borderColor: theme.colors.accent,
    borderRadius: BORDER_RADIUS_MEDIUM,
    borderWidth: 3,
    marginBottom: 4,
    marginRight: 8,
  },
}));
